import React, { useState, useEffect } from 'react';
import { UserProfile, FeedbackEntry, ImpactReport } from '../types';
import { generateSmartSurvey, generateImpactReport } from '../services/geminiService';
import { BarChart2, MessageSquare, TrendingUp, CheckCircle, Clock, Send, Sparkles, Loader2, ArrowUp, ArrowDown, Minus } from 'lucide-react';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const ImpactHub: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState<'feedback' | 'reports'>('feedback');
  const [surveyAnswer, setSurveyAnswer] = useState('');
  const [loadingSurvey, setLoadingSurvey] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    // Check if we need a new survey question
    const today = new Date().toISOString().split('T')[0];
    if (!user.feedbackSystem.pendingSurveyQuestion && user.feedbackSystem.lastSurveyDate !== today) {
       handleGenerateSurvey();
    }
  }, []);

  const handleGenerateSurvey = async () => {
    setLoadingSurvey(true);
    try {
      const question = await generateSmartSurvey(user);
      onUpdateUser({
        ...user,
        feedbackSystem: {
          ...user.feedbackSystem,
          pendingSurveyQuestion: question
        }
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSurvey(false);
    }
  };

  const handleSubmitFeedback = () => {
    if (!user.feedbackSystem.pendingSurveyQuestion || !surveyAnswer.trim()) return;

    const newFeedback: FeedbackEntry = {
      id: Date.now().toString(),
      question: user.feedbackSystem.pendingSurveyQuestion,
      answer: surveyAnswer,
      timestamp: Date.now(),
      isImplemented: false
    };

    onUpdateUser({
      ...user,
      feedbackSystem: {
        ...user.feedbackSystem,
        pendingSurveyQuestion: null,
        lastSurveyDate: new Date().toISOString().split('T')[0],
        history: [newFeedback, ...user.feedbackSystem.history]
      }
    });
    setSurveyAnswer('');
  };

  const handleGenerateReport = async () => {
    setLoadingReport(true);
    try {
      const report = await generateImpactReport(user);
      if (report) {
        onUpdateUser({
          ...user,
          feedbackSystem: {
            ...user.feedbackSystem,
            reports: [report, ...user.feedbackSystem.reports]
          }
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingReport(false);
    }
  };

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
      <div className="flex items-center justify-between mb-6">
        <div>
           <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
             <BarChart2 className="w-6 h-6 text-teal-600" />
             تأثیر و بازخورد
           </h2>
           <p className="text-slate-500 text-sm mt-1">مشاهده رشد شخصی و بهبود عملکرد دستیار</p>
        </div>
        <div className="flex bg-white rounded-xl border border-slate-200 p-1">
            <button 
                onClick={() => setActiveTab('feedback')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'feedback' ? 'bg-slate-100 text-slate-800' : 'text-slate-500'}`}>
                بازخورد
            </button>
            <button 
                onClick={() => setActiveTab('reports')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'reports' ? 'bg-slate-100 text-slate-800' : 'text-slate-500'}`}>
                گزارش‌ها
            </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'feedback' && (
          <div className="space-y-6">
            {/* Active Survey Card */}
            <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-3xl p-8 text-white shadow-lg relative overflow-hidden">
               <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
               
               <div className="relative z-10">
                 <div className="flex items-center gap-2 mb-4 opacity-90">
                    <MessageSquare className="w-5 h-5" />
                    <span className="font-bold">سوال هوشمند امروز</span>
                 </div>
                 
                 {loadingSurvey ? (
                   <div className="flex items-center gap-2">
                     <Loader2 className="animate-spin" /> در حال طرح سوال...
                   </div>
                 ) : user.feedbackSystem.pendingSurveyQuestion ? (
                   <>
                     <h3 className="text-xl md:text-2xl font-bold leading-relaxed mb-6">
                       "{user.feedbackSystem.pendingSurveyQuestion}"
                     </h3>
                     <div className="flex gap-2">
                       <input 
                         value={surveyAnswer}
                         onChange={(e) => setSurveyAnswer(e.target.value)}
                         placeholder="پاسخ شما..."
                         className="flex-1 bg-white/20 backdrop-blur-md text-white placeholder:text-white/60 rounded-xl px-4 py-3 border border-white/30 focus:outline-none focus:bg-white/30 transition-all"
                         onKeyDown={(e) => e.key === 'Enter' && handleSubmitFeedback()}
                       />
                       <button 
                         onClick={handleSubmitFeedback}
                         disabled={!surveyAnswer.trim()}
                         className="bg-white text-teal-600 p-3 rounded-xl hover:bg-teal-50 disabled:opacity-50 transition-colors shadow-lg">
                         <Send className="w-5 h-5 rtl:-rotate-90" />
                       </button>
                     </div>
                   </>
                 ) : (
                   <div className="text-center py-4">
                     <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-80" />
                     <p className="text-lg font-bold">برای امروز کافیست!</p>
                     <p className="opacity-80 text-sm">ممنون که به رشد من کمک می‌کنید.</p>
                   </div>
                 )}
               </div>
            </div>

            {/* Feedback Loop History */}
            <div>
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-slate-400" />
                چرخه بازخورد (Feedback Loop)
              </h3>
              
              {user.feedbackSystem.history.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-3xl border border-dashed border-slate-200 text-slate-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>هنوز بازخوردی ثبت نشده است.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {user.feedbackSystem.history.map((item) => (
                    <div key={item.id} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${item.isImplemented ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                        {item.isImplemented ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-500 mb-1">{item.question}</p>
                        <p className="font-medium text-slate-800 mb-2">"{item.answer}"</p>
                        {item.isImplemented && (
                           <div className="bg-emerald-50 p-3 rounded-xl text-xs text-emerald-700 border border-emerald-100 flex gap-2 items-start">
                             <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
                             <span><strong>اجرا شد:</strong> {item.implementationNote || 'ما این ویژگی را بر اساس نظر شما بهبود دادیم.'}</span>
                           </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="space-y-6">
             <div className="flex justify-between items-center bg-teal-50 p-4 rounded-xl border border-teal-100 text-teal-800 text-sm">
                <p>گزارش‌های ماهانه نشان می‌دهند که چقدر در مسیر اهدافتان پیشرفت کرده‌اید.</p>
                <button 
                  onClick={handleGenerateReport}
                  disabled={loadingReport}
                  className="px-4 py-2 bg-white border border-teal-200 rounded-lg text-teal-600 font-bold hover:bg-teal-100 transition-colors flex items-center gap-2"
                >
                  {loadingReport ? <Loader2 className="animate-spin w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                  تولید گزارش جدید
                </button>
             </div>

             {user.feedbackSystem.reports.length === 0 ? (
                <div className="text-center py-20 text-slate-400">
                   <BarChart2 className="w-16 h-16 mx-auto mb-4 opacity-20" />
                   <p>هنوز گزارشی موجود نیست.</p>
                </div>
             ) : (
                user.feedbackSystem.reports.map((report) => (
                  <div key={report.id} className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden mb-6 animate-fade-in">
                     <div className="p-6 border-b border-slate-50 flex justify-between items-center">
                        <h3 className="font-bold text-lg text-slate-800">{report.periodName}</h3>
                        <span className="text-xs text-slate-400">{new Date(report.dateGenerated).toLocaleDateString('fa-IR')}</span>
                     </div>
                     <div className="p-6">
                        <p className="text-slate-600 text-sm leading-relaxed mb-6 bg-slate-50 p-4 rounded-xl italic border-r-4 border-teal-400">
                           {report.aiSummary}
                        </p>
                        
                        <div className="grid grid-cols-2 gap-4">
                           {report.metrics.map((metric, idx) => (
                             <div key={idx} className="p-4 rounded-2xl border border-slate-100 bg-white shadow-sm flex flex-col items-center text-center">
                                <span className="text-xs text-slate-500 mb-2">{metric.label}</span>
                                <div className={`text-2xl font-black mb-2 flex items-center gap-1 ${metric.trend === 'up' ? 'text-emerald-500' : metric.trend === 'down' ? 'text-red-500' : 'text-slate-600'}`}>
                                   {metric.value}
                                   {metric.trend === 'up' && <ArrowUp className="w-4 h-4" />}
                                   {metric.trend === 'down' && <ArrowDown className="w-4 h-4" />}
                                   {metric.trend === 'neutral' && <Minus className="w-4 h-4" />}
                                </div>
                                <p className="text-[10px] text-slate-400">{metric.description}</p>
                             </div>
                           ))}
                        </div>
                     </div>
                  </div>
                ))
             )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImpactHub;